const BODY_FONT =
  '14px ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'

/** Card horizontal padding (p-4 left + right). */
const CARD_PADDING_X = 32
/** Gap between the first two data columns (td pr-2). */
const COLUMN_GAP_PX = 8
/** Input horizontal padding (px-2 left + right). */
const INPUT_PADDING_X = 16
/** Native date input width in Chromium (trimmed for tighter layout). */
const DATE_COL_PX = 118
/** Prime effective-date column — native date inputs need the full control width. */
const PRIME_DATE_COL_PX = 148
/** Rate input fixed width (w-20). */
const RATE_INPUT_PX = 80
/** Flex gap between rate input and % sign (gap-1). */
const RATE_PERCENT_GAP_PX = 4
/** Minus + plus icon buttons in the holidays action column. */
const HOLIDAY_ACTION_COL_PX = 56

function measureText(text: string, font = BODY_FONT): number {
  if (!text) return 0
  if (typeof document === 'undefined') {
    return text.length * 7.5
  }
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return text.length * 7.5
  ctx.font = font
  return ctx.measureText(text).width
}

function inputColumnWidth(texts: string[]): number {
  const content = texts.reduce((widest, text) => Math.max(widest, measureText(text)), 0)
  return Math.ceil(content + INPUT_PADDING_X)
}

export interface ThreeColumnLayout {
  col1Px: number
  col2Px: number
  col3Px: number
  cardMinPx: number
}

export function computePrimeCardLayout(
  rows: Array<{ rate: number }>,
): ThreeColumnLayout {
  const rateTexts = rows.map((row) => (row.rate * 100).toFixed(2))
  const rateContentPx = inputColumnWidth(rateTexts)
  const rateFieldPx = Math.max(RATE_INPUT_PX, rateContentPx)
  const percentPx = measureText('%')
  const col1Px = PRIME_DATE_COL_PX
  const col2Px = rateFieldPx + RATE_PERCENT_GAP_PX + percentPx
  const col3Px = Math.ceil(measureText('Remove'))
  const cardMinPx = CARD_PADDING_X + col1Px + COLUMN_GAP_PX + col2Px

  return { col1Px, col2Px, col3Px, cardMinPx }
}

export function computeIssuanceCardLayout(
  rows: Array<{ amount: number }>,
  formatAmount: (amount: number) => string,
): ThreeColumnLayout {
  const amountTexts = rows.map((row) => formatAmount(row.amount))
  const col1Px = DATE_COL_PX
  const col2Px = inputColumnWidth(amountTexts) * 2
  const col3Px = Math.ceil(measureText('Remove'))
  const cardMinPx = CARD_PADDING_X + col1Px + COLUMN_GAP_PX + col2Px

  return { col1Px, col2Px, col3Px, cardMinPx }
}

export function computeHolidaysCardLayout(
  rows: Array<{ name: string }>,
): ThreeColumnLayout {
  const nameTexts = rows.map((row) => row.name).filter((name) => name.length > 0)
  const col1Px = DATE_COL_PX
  const col2Px = inputColumnWidth(nameTexts.length > 0 ? nameTexts : ['Custom holiday'])
  const col3Px = HOLIDAY_ACTION_COL_PX
  const cardMinPx =
    CARD_PADDING_X + col1Px + COLUMN_GAP_PX + col2Px + COLUMN_GAP_PX + col3Px

  return { col1Px, col2Px, col3Px, cardMinPx }
}
